import { ImgHTMLAttributes, forwardRef, useCallback, useLayoutEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

/**
 * <img> that fades and settles in once decoded (the frame shows a shimmer
 * until then). Images already in the browser cache render immediately with no
 * animation, so revisits never flicker.
 */
export const TenantImage = forwardRef<HTMLImageElement, ImgHTMLAttributes<HTMLImageElement>>(
  ({ className, src, onLoad, onError, ...props }, forwardedRef) => {
    const localRef = useRef<HTMLImageElement | null>(null);
    const [loadedSrc, setLoadedSrc] = useState<string | undefined>(undefined);

    const setRefs = useCallback(
      (node: HTMLImageElement | null) => {
        localRef.current = node;
        if (typeof forwardedRef === "function") forwardedRef(node);
        else if (forwardedRef) forwardedRef.current = node;
      },
      [forwardedRef],
    );

    // Cached images are `complete` before first paint — skip the fade for them.
    useLayoutEffect(() => {
      const img = localRef.current;
      if (img?.complete && img.naturalWidth > 0) setLoadedSrc(src);
    }, [src]);

    return (
      <img
        ref={setRefs}
        src={src}
        className={cn("tp-img", className)}
        data-loaded={loadedSrc === src ? "true" : "false"}
        onLoad={(event) => {
          setLoadedSrc(src);
          onLoad?.(event);
        }}
        onError={(event) => {
          // Never leave a broken image invisible behind a shimmer.
          setLoadedSrc(src);
          onError?.(event);
        }}
        {...props}
      />
    );
  },
);
TenantImage.displayName = "TenantImage";
